import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, Loader2, Plus, Trash2, Upload } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { useAuth } from "../../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RegistriDialog({ open, onOpenChange, token, apiBase = "/api/stampe-mensili" }) {
  const { activeStruttura } = useAuth();
  const [registri, setRegistri] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nome, setNome] = useState("");
  const [categoria, setCategoria] = useState("generico");

  const fetchRegistri = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}${apiBase}/registri`, { headers: { Authorization: `Bearer ${token}` } });
      setRegistri(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [token, activeStruttura?.id, apiBase]); // eslint-disable-line react-hooks/exhaustive-deps -- refetch al cambio struttura

  useEffect(() => { if (open) fetchRegistri(); }, [open, fetchRegistri]);

  const create = async () => {
    if (nome.trim().length < 2) { toast.error("Nome minimo 2 caratteri"); return; }
    try {
      await axios.post(`${API_URL}${apiBase}/registri`, { nome: nome.trim(), categoria, frequenza: "mensile" }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Registro creato");
      setNome("");
      fetchRegistri();
    } catch {
      toast.error("Errore creazione");
    }
  };

  const uploadModello = async (registroId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API_URL}${apiBase}/registri/${registroId}/modello`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      toast.success("Modello caricato");
      fetchRegistri();
    } catch {
      toast.error("Errore upload");
    }
  };

  const openModello = async (registroId) => {
    try {
      const { data } = await axios.get(`${API_URL}${apiBase}/registri/${registroId}/modello`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
    } catch {
      toast.error("Impossibile aprire il modello");
    }
  };

  const deleteRegistro = async (registroId) => {
    if (!window.confirm("Eliminare questo registro?")) return;
    try {
      await axios.delete(`${API_URL}${apiBase}/registri/${registroId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Registro eliminato");
      fetchRegistri();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Gestisci registri personalizzati</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Nome registro</Label>
              <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Es. Registro farmaci" />
            </div>
            <div>
              <Label>Categoria</Label>
              <Select value={categoria} onValueChange={setCategoria}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="generico">Generico</SelectItem>
                  <SelectItem value="sanitario">Sanitario</SelectItem>
                  <SelectItem value="igienico">Igienico-sanitario</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={create} className="w-full"><Plus className="mr-2 h-4 w-4" />Aggiungi registro</Button>
          {loading ? <Loader2 className="mx-auto h-6 w-6 animate-spin" /> : (
            <div className="max-h-48 space-y-2 overflow-y-auto">
              {registri.map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <p className="text-sm font-medium">{r.nome}</p>
                    <p className="text-xs text-slate-400">{r.categoria} · {r.frequenza || "mensile"}{r.modello_name ? ` · ${r.modello_name}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {r.modello_path && (
                      <button type="button" className="rounded p-1 hover:bg-slate-100" title="Apri modello" onClick={() => openModello(r.id)}>
                        <Eye className="h-4 w-4 text-slate-500" />
                      </button>
                    )}
                    <label className="cursor-pointer rounded p-1 hover:bg-slate-100" title={r.modello_path ? "Sostituisci modello" : "Carica modello"}>
                      <Upload className="h-4 w-4 text-slate-500" />
                      <input type="file" className="hidden" accept=".pdf" onChange={(e) => e.target.files?.[0] && uploadModello(r.id, e.target.files[0])} />
                    </label>
                    <button type="button" className="rounded p-1 hover:bg-red-50" title="Elimina registro" onClick={() => deleteRegistro(r.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Chiudi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
